import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { WorkbookDomain } from '@mathflat/domain/@entities/(Content)/Workbook/domain'
import { observer } from 'mobx-react'
import { useEffect } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router'

import { routeName, type studentWorkbookDetailPathParams } from '~/@common/constants/route'
import { useLearningTime } from '~/@common/hooks/useLearningTime'
import { useRepository, useSetRepository } from '~/@common/hooks/useRepository'
import {
  resolveViewerMode,
  resolveWorkbookViewRoute,
  resolveWorkbookWriteKey,
} from '~/@common/models/(Permission)/permissionUtils'
import { errorHandlerService } from '~/@common/services'
import { useLastLocationManager } from '~/@common/services/(LastLocationManager)'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import GlobalHeader from '~/@pages/@widgets/(Navigation)/GlobalHeader/GlobalHeader'
import { StudentLearningContentBackConfirmModal } from '~/@pages/student/@widgets/learningContent/StudentLearningContentBackConfirmModal'

import { StudentWorkbookDetailService } from '../detail/@service/StudentWorkbookDetail.service'
import { StudentWorkbookViewerService } from './@service/StudentWorkbookViewer.service'
import StudentWorkbookViewerBody from './@widgets/StudentWorkbookViewerBody'

const StudentWorkbookViewerPage = () => {
  useLearningTime()
  const { studentWorkbookId, revisionId, pageNumber } =
    useParams<typeof studentWorkbookDetailPathParams>()
  const locationManager = useLastLocationManager()
  const navigate = useNavigate()

  const service = useSetRepository(
    StudentWorkbookDetailService<
      WorkbookDomain.TYPE['커스텀시그니처교재'] | WorkbookDomain.TYPE['학원그룹교재']
    >,
    pageNumber,
  )
  const viewerService = useRepository(StudentWorkbookViewerService)

  const defaultPrevRouteName =
    service.workbookDetail?.workbook.type === WorkbookDomain.TYPE.학원그룹교재
      ? routeName.student.academyGroupStudentWorkbookList
      : service.workbookDetail?.workbook.type === WorkbookDomain.TYPE.커스텀시그니처교재
        ? routeName.student.customSignatureStudentWorkbookList
        : routeName.student.generalStudentWorkbookList

  const navigateBack = () => {
    if (!locationManager.prevState?.location.pathname) {
      navigate(defaultPrevRouteName)
      return
    }
    navigate(-1)
  }

  useEffect(() => {
    if (pageNumber) {
      service.setCurrentPageNumber(pageNumber)
    } else {
      navigate(defaultPrevRouteName)
    }
  }, [pageNumber, defaultPrevRouteName])

  useEffect(() => {
    if (service && commonRepo.studentId && studentWorkbookId && revisionId) {
      service.fetchSetWorkbookDetail(commonRepo.studentId, {
        studentWorkbookId,
        revisionId,
      })
    }
  }, [service, studentWorkbookId, revisionId, commonRepo.studentId])

  // 진입 가드 — SIGNATURE_WORKBOOK_PDF 차단된 시그니처 워크북은 detail 라우트로 replace.
  // 학원그룹은 viewer 유지. parent/student prefix 는 워크북 type 별 write key 로 판정해서
  // 시그니처 등 타입에서 오분기되지 않도록 함.
  const workbookType = service.workbookDetail?.workbook.type
  const view = workbookType ? resolveWorkbookViewRoute(workbookType, commonRepo.permission) : null
  const viewerMode = workbookType
    ? resolveViewerMode(commonRepo.permission, resolveWorkbookWriteKey(workbookType))
    : 'student'
  // detail 로 곧 redirect 될 진입이면 viewer 전용 fetch 들도 같이 막아야 한다.
  // 안 그러면 redirect 가 끝나기 전에 viewer 전용 요청이 한 번 나가 403/불필요 호출이 생긴다.
  const shouldRedirectToDetail = view === 'detail'

  useEffect(() => {
    if (shouldRedirectToDetail) return
    if (
      service &&
      service.workbookDetail &&
      commonRepo.studentId &&
      studentWorkbookId &&
      revisionId &&
      pageNumber
    ) {
      const _pageNumber = parseInt(pageNumber)
      if (!isNaN(_pageNumber)) {
        service.fetchSetProblemScoringOfCurrentPage({
          studentId: commonRepo.studentId,
          studentWorkbookId,
          revisionId,
          pageNumber: _pageNumber,
        })
      }
    }
  }, [service, service.workbookDetail, studentWorkbookId, revisionId, pageNumber])

  useEffect(() => {
    if (shouldRedirectToDetail) return
    const workbookId = service.workbookDetail?.workbook.id
    const workbookType = service.workbookDetail?.workbook.type
    if (!workbookId || !workbookType) {
      return
    }
    viewerService.fetchWorkbookPages(workbookId, workbookType).catch(errorHandlerService.handle)
  }, [service.workbookDetail?.workbook.id, viewerService])

  useEffect(() => {
    if (!workbookType || !studentWorkbookId || !revisionId || !pageNumber) return
    if (!shouldRedirectToDetail) return

    const detailRoute =
      viewerMode === 'parent'
        ? routeName.parent.studentWorkbookDetail
        : routeName.student.studentWorkbookDetail

    navigate(generatePath(detailRoute, { studentWorkbookId, revisionId, pageNumber }), {
      replace: true,
    })
  }, [workbookType, view, viewerMode, studentWorkbookId, revisionId, pageNumber, navigate])

  if (!service.workbookDetail) return

  const headerTitle = service.workbookDetail
    ? `${service.workbookDetail?.round}회차 | ${service.workbookDetail?.workbook.title}`
    : ''

  return (
    <S.Container>
      <PortalRootLayoutHeader>
        {!viewerService.isFullscreenMode && (
          <GlobalHeader
            prevLinkProps={{
              to: defaultPrevRouteName,
              onClick: (e) => {
                e.preventDefault()
                if (
                  service.problemScoringViewOption &&
                  !service.problemScoringViewOption.채점불가능 &&
                  service.currentPage?.problemScoringColl &&
                  service.currentPage.problemScoringColl.toScoredArr.length > 0
                ) {
                  modalService.openModal(
                    <StudentLearningContentBackConfirmModal
                      defaultPrevRouteName={defaultPrevRouteName}
                      onSubmit={() => {
                        if (!(studentWorkbookId && revisionId)) {
                          navigateBack()
                          return
                        }

                        service.onSubmit({
                          studentWorkbookId,
                          revisionId,
                        })
                      }}
                    />,
                    {
                      modalName: '교재 - 채점 - 입력 내용 남았을 때 답안제출 확인',
                    },
                  )
                  return
                }
                navigateBack()
                return
              },
            }}
            SlotCenter={
              <div
                css={css`
                  display: -webkit-box;
                  overflow: hidden;
                  word-break: break-all;
                  -webkit-line-clamp: 1;
                  -webkit-box-orient: vertical;
                  text-overflow: ellipsis;
                `}
              >
                {headerTitle}
              </div>
            }
          />
        )}
      </PortalRootLayoutHeader>
      <StudentWorkbookViewerBody />
    </S.Container>
  )
}

export default observer(StudentWorkbookViewerPage)

const S = {
  Container: styled.div`
    height: 100%;
  `,
}
